import React, { createContext, useEffect, useState } from "react";

import { FinancialData } from "@eave/shared/types/api";
import { thereWasAProblem } from "@eave/shared/utils/alerts";

import Spinner from "web_modules/shared/Spinner";

import { getFinancialData, updateFinancialData } from "./api";

interface IFinancialDataContext {
  busy: boolean;
  financialData: FinancialData | null;
  updateData(data: FinancialData): Promise<void | {}>;
}

export const FinancialDataContext = createContext<IFinancialDataContext>({
  busy: false,
  financialData: null,
  updateData: (_: FinancialData) => Promise.resolve(null),
});

const FinancialDataProvider = ({ appId, children }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);

  const [financialData, setFinancialData] = useState<FinancialData | null>(null);

  const loadFinancialData = async () => {
    const newFinancialData = await getFinancialData(appId);
    setFinancialData(newFinancialData);
  };

  const updateData = async (data: FinancialData): Promise<void | {}> => {
    setBusy(true);

    try {
      await updateFinancialData(appId, data);
      return loadFinancialData();
    } catch (e) {
      thereWasAProblem("updating the information");
      throw e;
    } finally {
      setBusy(false);
    }
  };

  // initial page load
  useEffect(() => {
    setLoading(true);

    loadFinancialData()
      .catch(e => {
        thereWasAProblem("loading this page");
        throw e;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [appId]);

  if (loading) {
    return <Spinner active />;
  }

  return (
    <FinancialDataContext.Provider value={{ busy, financialData, updateData }}>
      {children}
    </FinancialDataContext.Provider>
  );
};

export default FinancialDataProvider;
